import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { DrillPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";

export type DrillPageHeaderTextsVariant = "portrait" | "landscape" | "landscape-compact";

// Keyed by drill kind rather than a ternary chain: a `Record<DrillType["kind"], …>` fails to
// compile when a kind is added, instead of silently falling through to the meaning copy.
const HEADER_TEXTS_BY_DRILL_KIND: Record<
  DrillType["kind"],
  { headerText: string; subtextText: string }
> = {
  meaning: {
    headerText: "MEANING",
    subtextText: "What does this character mean?",
  },
  pronunciation: {
    headerText: "PRONUNCIATION",
    subtextText: "How do you pronounce this character?",
  },
  "sentence-glyphs": {
    headerText: "Sentence Practice",
    subtextText: "Write this sentence in Chinese.",
  },
  "sentence-speech": {
    headerText: "Speaking Practice",
    subtextText: "Say this sentence in Chinese.",
  },
  "word-sentence": {
    headerText: "Word Review",
    subtextText: "Which characters spell this word?",
  },
};

export function DrillPageHeaderTexts({
  section = "both",
  variant,
}: {
  section?: "both" | "heading" | "prompt";
  variant: DrillPageHeaderTextsVariant;
}) {
  const [drillType] = useContextSelectors(DrillPageContext, (c) => c.drillType);

  const { headerText, subtextText } = HEADER_TEXTS_BY_DRILL_KIND[drillType.kind];

  // Glyph review labels sit between the card's replay and glyph-style controls.
  if (drillType.kind === "meaning" || drillType.kind === "pronunciation") {
    return (
      <>
        {section !== "prompt" ? (
          <Text
            adjustsFontSizeToFit
            className="shrink-0 text-center font-sans text-base tracking-widest text-muted-foreground"
            colorVariant="default"
            numberOfLines={1}
          >
            {headerText}
          </Text>
        ) : null}
        {section !== "heading" ? (
          <Text className="shrink-0 py-2 text-center text-base text-muted-foreground">
            {subtextText}
          </Text>
        ) : null}
      </>
    );
  }

  // Explicit `text-foreground` is load-bearing: with only `colorVariant="default"` the header
  // relied on the cva base color, which uniwind drops here once an arbitrary `text-[Npx]` size is
  // in the className — the heading then renders the platform-default black and is invisible on the
  // dark-mode review card. Match the rest of the app's large headings.
  const headerClassName =
    variant === "portrait"
      ? "mb-2 text-center font-sans text-[28px] text-foreground"
      : variant === "landscape-compact"
        ? "text-center font-sans text-[22px] leading-tight text-foreground"
        : "text-center font-sans text-[36px] text-foreground";

  const subtextClassName =
    variant === "portrait"
      ? "mb-6 text-center font-sans text-[16px]"
      : variant === "landscape-compact"
        ? "text-center font-sans text-[13px] leading-tight"
        : "text-center font-sans text-[16px]";

  return (
    <>
      <Text className={headerClassName} colorVariant="default">
        {headerText}
      </Text>
      <Text className={subtextClassName} colorVariant="muted">
        {subtextText}
      </Text>
    </>
  );
}
